import numeri from 'numeri';

const directions = [
  { dx: 1, dy: 0, name: 'right', emptyDx: 0, emptyDy: -1, xOff: 0, yOff: 0 },
  { dx: 0, dy: 1, name: 'down', emptyDx: 1, emptyDy: 0, xOff: 1, yOff: 0 },
  { dx: -1, dy: 0, name: 'left', emptyDx: 0, emptyDy: 1, xOff: 1, yOff: 1 },
  { dx: 0, dy: -1, name: 'up', emptyDx: -1, emptyDy: 0, xOff: 0, yOff: 1 },
];

const makeSigma = (rng, minR, maxR) => {
  const a = 1;
  const b = 1 / a;
  return rng.float(minR ** b, maxR ** b) ** a;
};

const makeCov = (rng, gaussMinR, gaussMaxR) => {
  const sigma0 = makeSigma(rng, gaussMinR, gaussMaxR);
  const sigma1 = makeSigma(rng, gaussMinR, gaussMaxR);
  const gaussR = Math.sqrt(sigma0 * sigma0 + sigma1 * sigma1);
  const theta = rng.float(0, 1) * Math.PI / 2;
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  return {
    x2r: 1 / sigma0 / sigma0,
    y2r: 1 / sigma1 / sigma1,
    gaussR,
    rotMat: numeri.fromFlat([cos, sin, -sin, cos], [2, 2]),
  };
};

const maskAt = (mask, scaleRatio, x, y) => {
  const xf = Math.floor(x / scaleRatio);
  const yf = Math.floor(y / scaleRatio);
  const result = mask.get(yf, xf);
  if (result === undefined) {
    throw Error('oh no');
  }
  return result;
};

const addGaussian = (args) => {
  const {
    x,
    y,
    subUnitScores,
    x2r,
    y2r,
    gaussR,
    rotMat,
    idx,
    mask,
    maskScale,
    zScore,
    scale,
  } = args;

  const [yh, xh] = subUnitScores.shape;
  const scaleRatio = scale / maskScale;
  const vec = numeri.vector([0, 0]);
  const bigR = gaussR * zScore;
  const xf0 = Math.max(Math.ceil(x - bigR), 0);
  const xf1 = Math.min(Math.floor(x + bigR), xh - 1);
  for (let xf = xf0; xf <= xf1; xf++) {
    const dx = xf - x;
    vec.set([0], dx);
    const subr = Math.sqrt(bigR * bigR - dx * dx);
    const yf0 = Math.max(Math.ceil(y - subr), 0);
    const yf1 = Math.min(Math.floor(y + subr), yh - 1);
    for (let yf = yf0; yf <= yf1; yf++) {
      const maskVal = maskAt(
        mask,
        scaleRatio,
        xf,
        yf,
      );
      if (!maskVal) {
        continue;
      }
      const dy = yf - y;
      vec.set([1], dy);
      const rotated = numeri.matMulVec(rotMat, vec).mapInPlace((val) => val * val);
      const r2 = rotated.get(0) * x2r + rotated.get(1) * y2r;
      const g = Math.exp(-r2 / 2);
      subUnitScores.update([yf, xf, idx], (z) => z + g);
    }
  }
};

const addIslandScores = (subUnitScores, rng, nUnit, island, config) => {
  const {
    mask,
    scale: maskScale,
  } = island;
  const {
    scale,
    shiftRatio,
    gaussMinRatio,
    gaussMaxRatio,
    padding,
    minNGauss,
    maxNGauss,
  } = config;

  const [height, width] = subUnitScores.shape;
  const xs = [];
  const ys = [];
  const minDim = Math.min(width, height);
  const shiftSize = shiftRatio * minDim;
  const gaussMinR = gaussMinRatio * minDim;
  const gaussMaxR = gaussMaxRatio * minDim;
  const minDist = 0.22 * Math.sqrt(width * height);
  const scaleRatio = scale / maskScale;
  const eps = 0.00001;
  const epsSlope = eps / Math.sqrt(1 + width * width + height * height);
  for (let idx = 0; idx < nUnit; idx++) {
    let x;
    let y;
    let good = false;
    let tries = 0;
    while (!good && tries < 30) {
      x = rng.float(padding * width, (1 - padding) * width);
      y = rng.float(padding * height, (1 - padding) * height);

      if (maskAt(mask, scaleRatio, x, y)) {
        let adequatelySpaced = true;
        for (let idx1 = 0; idx1 < idx; idx1++) {
          const dx = xs[idx1] - x;
          const dy = ys[idx1] - y;
          if (Math.sqrt(dx * dx + dy * dy) < minDist) {
            adequatelySpaced = false;
            break;
          }
        }
        good = adequatelySpaced;
      }
      tries++;
    }

    for (let i = 0; i < height; i++) {
      for (let j = 0; j < width; j++) {
        if (maskAt(mask, scaleRatio, j, i)) {
          const dx = j - x;
          const dy = i - y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const incr = eps - epsSlope * dist;
          subUnitScores.update([i, j, idx], (z) => z + incr);
        }
      }
    }

    xs.push(x);
    ys.push(y);

    const targetNGauss = rng.int(minNGauss, maxNGauss + 1);
    let nGauss = 0;
    while (nGauss < targetNGauss) {
      const shiftX = rng.float(-shiftSize, shiftSize);
      const shiftY = rng.float(-shiftSize, shiftSize);
      if (shiftX * shiftX + shiftY * shiftY > shiftSize * shiftSize) {
        continue;
      }
      const xShifted = x + shiftX;
      const yShifted = y + shiftY;
      const cov = makeCov(rng, gaussMinR, gaussMaxR);
      addGaussian({
        ...config,
        ...cov,
        subUnitScores,
        x: xShifted,
        y: yShifted,
        idx,
        mask,
        maskScale,
      });
      nGauss++;
    }
  }

  return { xs, ys };
};

class Unit {
  traverse(f) {
    f(this);
    this.children.forEach((child) => child.traverse(f));
  }

  incr(key, amount, rng, options) {
    const { eccentricity, oneHot } = options;
    const n = this.children.length;
    this.meta[key] = (this.meta[key] || 0) + amount;

    if (n === 0) {
      return;
    }

    if (eccentricity) {
      const softmax = numeri.empty([n])
        .map(() => Math.exp(rng.float(0, 1) * eccentricity));
      let softsum = softmax.sum();
      softmax.mapInPlace((x) => Math.round(amount * x / softsum));
      softsum = softmax.sum();
      for (let i = 0; i < n; i++) {
        if (softsum === amount) {
          break;
        }
        const delta = Math.max(amount - softsum, -softmax.get(i));
        softmax.update([i], (x) => x + delta);
        softsum += delta;
      }
      for (let i = 0; i < n; i++) {
        this.children[i].incr(key, softmax.get(i), rng, options);
      }
    } else if (oneHot) {
      const idx = rng.int(0, n);
      this.children[idx].incr(key, amount, rng, options);
    }
  }

  generateChildren(nUnit, rng, config) {
    const {
      thresh,
      scale,
    } = config;

    const {
      xMin,
      xMax,
      yMin,
      yMax,
      scale: parentScale,
    } = this.meta;

    const nIsland = this.islands.length;
    nUnit = Math.max(nUnit, nIsland);
    const nUnitPerIsland = numeri.fill([nIsland], 1).toNested();
    const remainingInds = rng.inds(
      nIsland,
      nUnit - nIsland,
      { weight: this.islands.map((x) => x.nPoints) },
    );
    remainingInds.forEach((i) => {
      nUnitPerIsland[i] += 1;
    });

    const scaleRatio = scale / parentScale;

    const yh = (yMax - yMin) * scaleRatio;
    const xh = (xMax - xMin) * scaleRatio;
    const subUnitScores = numeri.zeros([
      yh,
      xh,
      nUnit,
    ]);

    let offset = 0;
    const xs = [];
    const ys = [];
    for (let i = 0; i < nIsland; i++) {
      const island = this.islands[i];
      const { xMin: ixMin, xMax: ixMax, yMin: iyMin, yMax: iyMax } = this.islands[i];
      const x0 = Math.round((ixMin - xMin) * scaleRatio);
      const x1 = Math.round((ixMax - xMin) * scaleRatio);
      const y0 = Math.round((iyMin - yMin) * scaleRatio);
      const y1 = Math.round((iyMax - yMin) * scaleRatio);
      const thisNUnit = nUnitPerIsland[i];

      const { xs: iXs, ys: iYs } = addIslandScores(
        subUnitScores.slice([y0, y1], [x0, x1], [offset]),
        rng,
        thisNUnit,
        island,
        config,
      );
      for (let j = 0; j < thisNUnit; j++) {
        xs.push(iXs[j]);
        ys.push(iYs[j]);
      }
      offset += nUnitPerIsland[i];
    }

    const { arg, values } = subUnitScores.argmax({
      axis: 2,
      includeValues: true,
    });
    arg.elemwiseBinaryOpInPlace(
      values,
      (argmin, min) => min > thresh ? argmin : -1,
    );

    const children = [];
    for (let i = 0; i < nUnit; i++) {
      children.push(new ChildUnit(
        `${this.id}-${i}`,
        this,
        [], // will get populated later
        {
          scale,
          xMin: xh,
          xMax: 0,
          yMin: yh,
          yMax: 0,
          area: 0,
          x: xs[i],
          y: ys[i],
          neighbors: new Set(),
          idx: i,
        },
      ));
    }

    const perPixArea = 1 / scale / scale;
    for (let i = 0; i < yh; i++) {
      for (let j = 0; j < xh; j++) {
        const idx = arg.get(i, j);
        if (idx !== -1) {
          const meta = children[idx].meta;
          meta.xMin = Math.min(meta.xMin, j);
          meta.xMax = Math.max(meta.xMax, j + 1);
          meta.yMin = Math.min(meta.yMin, i);
          meta.yMax = Math.max(meta.yMax, i + 1);
          meta.area += perPixArea;
        }
      }
    }

    this.pixelMap = arg;
    this.children = children;

    this.computeShapes();

    this.shapes.forEach((shape) => {
      if (shape.idx === -1) {
        return;
      }
      shape.scale = scale;
      const child = this.children[shape.idx];
      child.islands.push(shape);
      const { neighbors } = child.meta;
      Array.from(shape.neighbors).forEach((j) => {
        neighbors.add(j);
      });
    });
  }

  computeShapes() {
    const [yh, xh] = this.pixelMap.shape;
    const shape8s = [];
    const shape4s = [];
    const visited8 = numeri.fill([yh, xh], undefined);
    const visited4 = numeri.fill([yh, xh], undefined);
    for (let i = 0; i < yh; i++) {
      for (let j = 0; j < xh; j++) {
        if (!visited8.get(i, j)) {
          const shape = this.bfsFrom(i, j, visited8, 8);
          shape8s.push(shape);
        }
        if (!visited4.get(i, j)) {
          const shape = this.bfsFrom(i, j, visited4, 4);
          shape4s.push(shape);
        }
      }
    }
    let shapesLeft = shape8s.filter((shape) => shape.depth === undefined);
    let minDepth = 0;

    while (shapesLeft.length > 0 && minDepth < 10) {
      let lastNShapesLeft;
      while (shapesLeft.length > 0 && shapesLeft.length !== lastNShapesLeft) {
        lastNShapesLeft = shapesLeft.length;

        shapesLeft.forEach((shape) => {
          const neighborShapes = Array.from(shape.neighborShapes);
          const neighDepthCounts = {};
          let minNeighDepth = Infinity;
          for (let i = 0; i < neighborShapes.length; i++) {
            const nShape = neighborShapes[i];
            const { depth: nDepth, idx: nIdx } = nShape;

            if (nDepth === undefined) {
              continue;
            }

            if (nIdx === -1) {
              shape.depth = nDepth;
              return;
            }
            minNeighDepth = Math.min(nDepth, minNeighDepth);
            if (!neighDepthCounts[nDepth]) {
              neighDepthCounts[nDepth] = 0;
            }
            neighDepthCounts[nDepth] ++;
          }

          if (neighborShapes.length === 1 && neighborShapes[0].depth !== undefined) {
            shape.depth = neighborShapes[0].depth + 1;
            return;
          }

          if (minNeighDepth !== Infinity && minNeighDepth + 1 === minDepth) {
            shape.depth = minDepth;
            return;
          }

          if (minNeighDepth !== Infinity && neighDepthCounts[minNeighDepth] > 1) {
            shape.depth = minNeighDepth;
          }
        });

        shapesLeft = shapesLeft.filter((shape) => shape.depth === undefined);
      }
      minDepth++;
    }

    this.shapes = shape8s
      .filter((shape) => shape.idx !== -1 || shape.depth > 0);
    this.shapes.forEach((shape) => {
      shape.kept = true;
      shape.shape4s = [];
    });
    this.shape4s = shape4s
      .filter((shape4) => {
        const { i, j } = shape4.point;
        const shape8 = visited8.get(i, j);
        const { kept, depth } = shape8;
        if (kept) {
          shape4.depth = depth;
          return true;
        }
        return false;
      });
  }

  bfsFrom(i, j, visited, adjNum) {
    const { pixelMap } = this;
    const [yh, xh] = pixelMap.shape;
    const idx = pixelMap.get(i, j);
    const points = [{ i, j }];
    let count = 0;
    let onEdge = false;
    const neighbors = new Set();
    const neighborShapes = new Set();
    let boundaryPt;
    let xMin = xh;
    let xMax = 0;
    let yMin = yh;
    let yMax = 0;
    let depth;

    const shape = {};
    visited.set([i, j], shape);

    let getNeigh;
    if (adjNum === 8) {
      getNeigh = (i0, j0) => {
        const xOffs = [0];
        const yOffs = [0];
        const result = [];
        if (i0 > 0) {
          yOffs.push(-1);
        }
        if (j0 > 0) {
          xOffs.push(-1);
        }
        if (i0 < yh - 1) {
          yOffs.push(1);
        }
        if (j0 < xh - 1) {
          xOffs.push(1);
        }
        yOffs.forEach((di) => {
          xOffs.forEach((dj) => {
            if (di !== 0 || dj !== 0) {
              result.push({ i: i0 + di, j: j0 + dj });
            }
          });
        });

        return result;
      };
    } else if (adjNum === 4) {
      getNeigh = (i0, j0) => {
        const result = [];
        if (i0 > 0) {
          result.push({ i: i0 - 1, j: j0 });
        }
        if (j0 > 0) {
          result.push({ i: i0, j: j0 - 1 });
        }
        if (i0 < yh - 1) {
          result.push({ i: i0 + 1, j: j0 });
        }
        if (j0 < xh - 1) {
          result.push({ i: i0, j: j0 + 1 });
        }
        return result;
      };
    }

    while (count < points.length) {
      const pt = points[count];
      xMin = Math.min(xMin, pt.j);
      xMax = Math.max(xMax, pt.j + 1);
      yMin = Math.min(yMin, pt.i);
      yMax = Math.max(yMax, pt.i + 1);
      const neighs = getNeigh(pt.i, pt.j);
      let isBorder = neighs.length < adjNum;
      if (isBorder) {
        depth = 0;
        onEdge = true;
      }
      for (let neighI = 0; neighI < neighs.length; neighI++) {
        const neigh = neighs[neighI];
        const idx0 = pixelMap.get(neigh.i, neigh.j);
        if (idx0 !== idx) {
          neighbors.add(idx0);
          isBorder = true;
          const neighborShape = visited.get(neigh.i, neigh.j);
          if (neighborShape) {
            neighborShapes.add(neighborShape);
            neighborShape.neighborShapes.add(shape);
          }
          continue;
        }
        if (visited.get(neigh.i, neigh.j)) {
          continue;
        }
        visited.set([neigh.i, neigh.j], shape);
        points.push(neigh);
      }
      if (isBorder) {
        boundaryPt = pt;
      }
      count++;
    }

    const mask = numeri.fill([yMax - yMin, xMax - xMin], false);
    points.forEach((point) => {
      mask.set([point.i - yMin, point.j - xMin], true);
    });

    const boundary = [];
    // only evaluate boundary for 4 adj
    if (adjNum === 4) {
      const b = Object.assign({}, boundaryPt);
      let curI = b.i;
      let curJ = b.j;
      let dir;

      const sameIdx = (otherI, otherJ) => {
        if (otherI < 0 || otherI >= yh || otherJ < 0 || otherJ >= xh) {
          return false;
        }
        return pixelMap.get(otherI, otherJ) === idx;
      };

      for (let initDir = 0; initDir < 4; initDir++) {
        const direction = directions[initDir];
        const i0 = b.i + direction.emptyDy;
        const j0 = b.j + direction.emptyDx;
        if (!sameIdx(i0, j0)) { // includes undefined
          dir = initDir;
          boundary.push({
            type: 'init',
            i: curI + direction.yOff,
            j: curJ + direction.xOff,
          });
          break;
        }
      }
      while (true) {
        const direction = directions[dir];
        const i0 = curI + direction.dy;
        const j0 = curJ + direction.dx;

        const ccw = (dir + 3) % 4; // counter-clockwise
        const ccwd = directions[ccw];
        const i1 = i0 + ccwd.dy;
        const j1 = j0 + ccwd.dx;
        const cw = (dir + 1) % 4; // clockwise
        const terminalI = i0 + direction.yOff;
        const terminalJ = j0 + direction.xOff;

        if (terminalI === boundary[0].i && terminalJ === boundary[0].j) {
          // back to starting point
          boundary.push({
            type: direction.name,
            i: terminalI,
            j: terminalJ,
          });
          break;
        }

        if (!sameIdx(i0, j0)) {
          // cw
          boundary.push({
            type: direction.name,
            i: terminalI,
            j: terminalJ,
          });
          dir = cw;
        } else if (!sameIdx(i1, j1)) {
          // straight
          curI = i0;
          curJ = j0;
        } else {
          // ccw
          boundary.push({
            type: direction.name,
            i: terminalI,
            j: terminalJ,
          });
          dir = ccw;
          curI = i1;
          curJ = j1;
        }
      }
    }

    Object.assign(shape, {
      point: points[0],
      boundary,
      idx,
      onEdge,
      neighbors,
      xMin,
      xMax,
      yMin,
      yMax,
      mask,
      nPoints: points.length,
      depth,
      neighborShapes,
    });
    return shape;
  }
}

// islands: list of each connected subset of points
//  belonging to this unit
// shapes: list of each connected subset of points
//  belonging to a single subunit
class RootUnit extends Unit {
  constructor(id, yRatio) {
    super();
    this.id = id;
    this.meta = {
      xMin: 0,
      xMax: 1,
      yMin: 0,
      yMax: yRatio,
      mask: numeri.fill([1, 1], true),
      scale: 1,
      nPoints: 1,
    };
    this.isRoot = true;

    this.islands = [this.meta];
    this.children = [];
    this.shapes = [];
  }

  islands() {
    return [this.meta];
  }
}

class ChildUnit extends Unit {
  constructor(id, parent, islands, meta) {
    super();
    this.id = id;
    this.parent = parent;
    this.islands = islands;
    this.meta = meta;

    this.children = [];
    this.shapes = [];
  }
}

export default RootUnit;



// WEBPACK FOOTER //
// src/components/Virus/Unit.js